import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { ArrowLeft, Store, Clock, Truck, MapPin, Loader2, AlertTriangle, ChevronRight } from "lucide-react";

function pence(p: number) { return `£${(p / 100).toFixed(2)}`; }

type CompanionStore = {
  store: {
    id: number;
    name: string;
    slug: string;
    imageUrl?: string | null;
    category?: string | null;
    address?: string | null;
    deliveryTimeMins?: number | null;
    minOrderPence?: number | null;
  };
  deliveryFeePence: number;
  distanceToAnchorKm?: number | null;
  safeUntilMs?: number | null;
  multiOrderDeliveryAddPence?: number | null;
};

export default function MultiOrderCompanionPicker() {
  const [, navigate] = useLocation();
  const { loginWithToken } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const anchorStoreId = Number(params.get("anchorStoreId"));
  const anchorSlug = params.get("anchorSlug") ?? "";
  const anchorName = decodeURIComponent(params.get("anchorName") ?? "this store");

  const [companions, setCompanions] = useState<CompanionStore[]>([]);
  const [loading, setLoading] = useState(true);
  const [ineligibleMsg, setIneligibleMsg] = useState<string | null>(null);
  const [selecting, setSelecting] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { void fetchCompanions(); }, [anchorStoreId]);

  async function ensureAuth(): Promise<string | null> {
    let token = localStorage.getItem("ayrshire_token");
    if (!token) {
      try {
        const res = await fetch("/api/auth/guest", { method: "POST", headers: { "Content-Type": "application/json" } });
        if (!res.ok) return null;
        const d = await res.json() as { token: string };
        token = d.token;
        localStorage.setItem("ayrshire_token", token);
        localStorage.setItem("ayrshire_is_guest", "1");
        void loginWithToken(token);
      } catch { return null; }
    }
    return token;
  }

  async function fetchCompanions() {
    setLoading(true);
    try {
      const token = await ensureAuth();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(`/api/multi-order/eligible-companions?anchorStoreId=${anchorStoreId}`, { headers });
      const d = await res.json() as { eligible?: boolean; message?: string; companions?: CompanionStore[] };
      if (!d.eligible) {
        setIneligibleMsg(d.message ?? "Combined ordering not available from this store right now.");
      } else {
        setCompanions(d.companions ?? []);
      }
    } catch {
      setIneligibleMsg("Could not load available stores. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleSelect(companion: CompanionStore) {
    setSelecting(companion.store.id);
    setError(null);
    try {
      const token = await ensureAuth();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch("/api/multi-basket", {
        method: "POST",
        headers,
        body: JSON.stringify({ anchorStoreId, companionStoreIds: [companion.store.id] }),
      });
      const d = await res.json() as { error?: string };
      if (!res.ok) {
        setError(d.error ?? "Could not start combined order. Please try again.");
        return;
      }
      navigate(
        `/order/${companion.store.slug}?multiAnchorStoreId=${anchorStoreId}&multiAnchorStoreName=${encodeURIComponent(anchorName)}&anchorSlug=${anchorSlug}&companionStoreId=${companion.store.id}`
      );
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSelecting(null);
    }
  }

  function closingWarning(safeUntilMs?: number | null): string | null {
    if (!safeUntilMs) return null;
    const mins = Math.floor((safeUntilMs - Date.now()) / 60000);
    if (mins <= 0) return "Closing now";
    if (mins <= 30) return `Closes in ${mins} min`;
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(`/order/${anchorSlug}`)}
            className="p-2 rounded-full hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-[28px] leading-tight font-bold tracking-[-1px] text-[#1e3a5f]">Add another store</h1>
            <p className="text-xs text-slate-500">Combined with {anchorName}</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-8">
        {/* Info card */}
        <div className="py-5 border-b border-slate-100 mb-6">
          <p className="font-semibold text-primary text-sm mb-0.5">One delivery, two stores</p>
          <p className="text-xs text-primary/70">
            Add a second store and pay together. Your driver collects both orders and delivers in one trip — saving on delivery fees.
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="animate-spin text-primary" />
          </div>
        )}

        {!loading && ineligibleMsg && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-8 text-center">
            <AlertTriangle size={32} className="mx-auto text-amber-500 mb-3" />
            <p className="font-semibold text-amber-800 mb-1">Not available right now</p>
            <p className="text-sm text-amber-700 mb-4">{ineligibleMsg}</p>
            <button
              onClick={() => navigate(`/order/${anchorSlug}`)}
              className="px-4 py-2 rounded-lg border border-amber-300 text-amber-700 text-sm font-medium hover:bg-amber-100 transition-colors"
            >
              Back to {anchorName}
            </button>
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && !ineligibleMsg && companions.length === 0 && (
          <div className="text-center py-16 text-slate-400">
            <Store size={40} className="mx-auto mb-3 opacity-40" />
            <p className="font-medium text-slate-500">No stores available to combine right now</p>
            <p className="text-sm mt-1">More stores are being added — check back soon.</p>
            <button
              onClick={() => navigate(`/order/${anchorSlug}`)}
              className="mt-4 px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-100 transition-colors"
            >
              Back to {anchorName}
            </button>
          </div>
        )}

        {!loading && companions.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Choose a store</p>
            <div className="space-y-3">
              {companions.map((c) => {
                const warning = closingWarning(c.safeUntilMs);
                const extraFee = c.multiOrderDeliveryAddPence ?? c.deliveryFeePence;
                const isSelecting = selecting === c.store.id;
                return (
                  <button
                    key={c.store.id}
                    onClick={() => { if (!selecting) void handleSelect(c); }}
                    disabled={!!selecting}
                    className="w-full text-left bg-white border-b border-slate-100 py-5 flex items-center gap-4 hover:bg-slate-50 transition-all disabled:opacity-60"
                  >
                    {c.store.imageUrl ? (
                      <img src={c.store.imageUrl} alt={c.store.name} className="w-24 h-24 rounded-xl object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-24 h-24 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Store size={22} className="text-slate-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="min-w-0">
                          <p className="font-bold text-[#1e3a5f] text-lg tracking-tight">{c.store.name}</p>
                          {c.store.category && <p className="text-xs text-slate-400">{c.store.category}</p>}
                        </div>
                        {warning && (
                          <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap">
                            {warning}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        {extraFee > 0 ? (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <Truck size={11} />+{pence(extraFee)} delivery
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-emerald-600 font-semibold">
                            <Truck size={11} />Free extra delivery
                          </span>
                        )}
                        {c.store.deliveryTimeMins && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <Clock size={11} />{c.store.deliveryTimeMins} min
                          </span>
                        )}
                        {c.distanceToAnchorKm != null && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <MapPin size={11} />{c.distanceToAnchorKm.toFixed(1)} km
                          </span>
                        )}
                        {(c.store.minOrderPence ?? 0) > 0 && (
                          <span className="text-xs text-slate-400">Min {pence(c.store.minOrderPence!)}</span>
                        )}
                      </div>
                    </div>
                    {isSelecting ? (
                      <Loader2 size={18} className="animate-spin text-primary flex-shrink-0" />
                    ) : (
                      <ChevronRight size={18} className="text-slate-300 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
